import {
  useEffect, useMemo, useState,
} from 'react'
import {
  useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query'
import {
  AlertCircle,
  BarChart3,
  Building2,
  Clock,
  CreditCard,
  EyeOff,
  Loader2,
  RotateCcw,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users,
  UserX,
  Zap,
} from 'lucide-react'
import {
  getApiErrorMessage,
  organizationService,
} from '../services/api'
import type {
  EligibleUserResponse,
  InviteResult,
  MyInviteResponse,
  OrganizationMemberResponse,
  OrganizationResponse,
  OrganizationStatsResponse,
  PendingInviteResponse,
  RedactedEntryResponse,
} from '../client'
import {
  useAuth,
} from '../hooks/useAuth'
import {
  useOrganizationAccess,
} from '../hooks/useOrganizationAccess'
import {
  CheckoutModal,
} from './CheckoutModal'
import {
  trackEvent,
} from '../analytics/posthog'
import {
  TeamSkillsSection,
} from './TeamSkillsSection'

const ORG_QUERY_KEY = ['organization']
const MEMBERS_QUERY_KEY = ['organization-members']
const STATS_QUERY_KEY = ['organization-stats']
const INVITES_QUERY_KEY = ['organization-invites']
const ELIGIBLE_QUERY_KEY = ['organization-eligible']
const MY_INVITES_QUERY_KEY = ['organization-my-invites']
const ACCESS_QUERY_KEY = ['organization-access']
const REDACTED_ENTRIES_QUERY_KEY = ['organization-redacted-entries']

const CHECKOUT_POLL_INTERVAL_MS = 2000
const CHECKOUT_POLL_MAX_ATTEMPTS = 8

const INVITE_OUTCOME_LABELS: Record<string, string> = {
  invited: "Invited — they'll join once they accept",
  already_member: 'Already on the team',
  already_invited: 'Invite already pending',
  invalid_email: "That doesn't look like an email address",
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function Badge({ label, tone, title }: {
  label: string
  tone: 'green' | 'gray' | 'red' | 'blue' | 'amber'
  title?: string
}): JSX.Element {
  const tones: Record<string, string> = {
    green: 'bg-green-50 text-green-700',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
  }
  return (
    <span title={title} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>
      {label}
    </span>
  )
}

// An "active" account only means it isn't closed. Catch has nothing to work
// with for a member who never granted Mail & Calendar (not set up), nor for
// one whose sign-in has since failed (needs reconnect).
function StatusBadge({ member }: { member: OrganizationMemberResponse }): JSX.Element {
  const status = member.user_status
  if (status === 'active' && !member.has_mail_calendar_access) {
    return <Badge label="Not set up" tone="amber" title="Hasn't connected Mail & Calendar yet" />
  }
  if (status === 'active' && member.needs_reauth) {
    return (
      <Badge
        label="Needs reconnect"
        tone="red"
        title="Their sign-in stopped working, so Catch can't reach their mail or calendar until they reconnect"
      />
    )
  }
  if (status === 'active') return <Badge label="Active" tone="green" />
  if (status === 'pending') return <Badge label="Pending" tone="amber" />
  return <Badge label="Deactivated" tone="gray" />
}

function BillingBadge({ source }: { source: string }): JSX.Element {
  if (source === 'team') return <Badge label="Team" tone="blue" />
  if (source === 'individual') return <Badge label="Individual" tone="gray" />
  if (source === 'no_seat') {
    return <Badge label="No seat" tone="gray" title="Runs the team without an assistant of their own, so the team isn't billed for them" />
  }
  return <Badge label="—" tone="gray" />
}

function SectionCard({ title, icon, subtitle, children }: {
  title: string
  icon: JSX.Element
  subtitle?: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-soft p-6">
      <div className="flex items-center gap-2">
        <span className="text-primary-500">{icon}</span>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
      <div className="mt-4">{children}</div>
    </div>
  )
}

function OverviewTile({ label, value, Icon }: {
  label: string
  value: string
  Icon: typeof Users
}): JSX.Element {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-soft p-5">
      <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="mt-3 text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight tabular-nums">
        {value}
      </div>
      <div className="mt-0.5 text-sm font-medium text-gray-500">{label}</div>
    </div>
  )
}

function PrimaryButton({ children, onClick, disabled, busy }: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
  busy?: boolean
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white px-4 py-2.5 text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
      {children}
    </button>
  )
}

function MyInvitesCard({ invites }: { invites: MyInviteResponse[] }): JSX.Element {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: MY_INVITES_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY })
  }
  const acceptMutation = useMutation({
    mutationFn: (inviteId: number) => organizationService.acceptInvite(inviteId),
    onSuccess: () => {
      trackEvent('team_invite_accepted')
      refresh()
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't accept that invite. Try again."))
    },
  })
  const declineMutation = useMutation({
    mutationFn: (inviteId: number) => organizationService.declineInvite(inviteId),
    onSuccess: () => {
      trackEvent('team_invite_declined')
      refresh()
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't decline that invite. Try again."))
    },
  })
  const busy = acceptMutation.isPending || declineMutation.isPending

  return (
    <div className="max-w-lg mx-auto mb-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-soft p-6">
        <div className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-gray-900">You're invited</h2>
        </div>
        <ul className="mt-3 divide-y divide-gray-100">
          {invites.map((invite) => (
            <li key={invite.id} className="py-3 flex items-center justify-between gap-3">
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">{invite.organization_name}</span>
                {' '}
                wants you on their team
                <span className="text-gray-400"> · invited {formatDate(invite.invited_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => declineMutation.mutate(invite.id)}
                  className="rounded-xl border border-gray-300 text-gray-700 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  Decline
                </button>
                <PrimaryButton
                  onClick={() => acceptMutation.mutate(invite.id)}
                  busy={busy}
                >
                  Join
                </PrimaryButton>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-gray-400">
          Joining puts your subscription on the team's billing. You can leave again anytime.
        </p>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </div>
    </div>
  )
}

function CreateTeamCard(): JSX.Element {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const createMutation = useMutation({
    mutationFn: (teamName: string) => organizationService.createOrganization(teamName),
    onSuccess: () => {
      trackEvent('team_created')
      void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['organization-access'] })
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't create your team. Try again."))
    },
  })

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-soft p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center">
          <Building2 className="w-6 h-6" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Set up your team</h2>
        <p className="mt-2 text-sm text-gray-500">
          Bring your whole company onto Catch — invite people, pay with one card,
          and see what everyone's assistant gets done.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setError(null)
            }}
            placeholder="Team name (e.g. Acme)"
            className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <PrimaryButton
            onClick={() => createMutation.mutate(name)}
            disabled={!name.trim()}
            busy={createMutation.isPending}
          >
            Create team
          </PrimaryButton>
        </div>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </div>
    </div>
  )
}

/**
 * "It's just me here" — the opt-out for a company directory that isn't a
 * company. Only rendered for an account whose identity provider actually
 * asserts a directory; a personal account is never auto-joined, so offering the
 * control there would describe a thing that doesn't happen.
 */
function NotACompanyCard({ declared, org }: {
  declared: boolean
  /** The auto-formed team this would dissolve, when the caller is its admin. */
  org?: OrganizationResponse
}): JSX.Element {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const declineMutation = useMutation({
    mutationFn: () => organizationService.declineAutoJoin(),
    onSuccess: () => {
      trackEvent('team_auto_join_declined')
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't turn that off. Try again."))
    },
  })
  // Two different actions behind one control. A team that formed on its own and
  // still has only its accidental admin on it is dissolved outright; any other
  // team just stops absorbing the directory and carries on existing — deleting
  // a team with other members on it would strip their coverage silently.
  const attached = org != null
  const willDissolve = attached && org.is_auto_formed && org.member_count === 1
  const confirmDecline = (): void => {
    if (attached && !window.confirm(
      willDissolve
        ? `Dissolve the ${org.name} team? ${org.billing.is_active
          ? 'The team plan goes back to being your own subscription — same card, same renewal date — and n'
          : 'N'}obody from your company will be added to a team automatically.`
        : `Stop adding people from your company to ${org.name} automatically? `
          + 'The team and everyone on it stay exactly as they are.',
    )) return
    declineMutation.mutate()
  }

  return (
    <div className="max-w-lg mx-auto mt-4 text-center">
      {declared ? (
        <p className="text-sm text-gray-500">
          You won't be added to a team automatically.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-500">
            {!attached
              ? "When someone else from your company joins Catch, we'll put you on one team together. Not a company?"
              : willDissolve
                ? "This team formed on its own when a second person from your company joined Catch. You didn't ask for it."
                : 'Everyone from your company who joins Catch is added to this team automatically.'}
          </p>
          <button
            type="button"
            disabled={declineMutation.isPending}
            onClick={confirmDecline}
            className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-gray-600 underline underline-offset-4 hover:text-gray-900 disabled:opacity-60"
          >
            {declineMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {!attached
              ? "It's just me — don't group us"
              : willDissolve
                ? "It's just me — dissolve this team"
                : 'Stop adding people automatically'}
          </button>
        </>
      )}
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
    </div>
  )
}

export function BillingCard({ org }: { org: OrganizationResponse }): JSX.Element {
  const queryClient = useQueryClient()
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Embedded checkout ends with a full top-level redirect back here, which
  // races the Stripe webhook: for a few seconds the org still looks
  // unsubscribed, and showing "Set up team billing" again invites a double
  // purchase (the /billing/success page plays this role for personal
  // checkout). Land on ?checkout=success and poll the org until the webhook
  // has confirmed before offering checkout again.
  const [finalizing, setFinalizing] = useState<boolean>(() => (
    new URLSearchParams(window.location.search).get('checkout') === 'success'
  ))

  useEffect(() => {
    if (!finalizing) return undefined
    window.history.replaceState(null, '', '/team')
    // Subscription checkout confirms only once Stripe is attached; a local
    // cardless plan was already "active" before checkout, so is_active alone
    // would stop polling before its subscription webhook arrived. The 0-seat
    // setup-mode checkout confirms via is_billing_armed instead.
    if (org.billing.has_stripe_subscription || org.billing.is_billing_armed) {
      setFinalizing(false)
      return undefined
    }
    let attempts = 0
    const interval = window.setInterval(() => {
      attempts += 1
      if (attempts > CHECKOUT_POLL_MAX_ATTEMPTS) {
        setFinalizing(false)
        return
      }
      void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
    }, CHECKOUT_POLL_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [
    finalizing,
    org.billing.has_stripe_subscription,
    org.billing.is_billing_armed,
    queryClient,
  ])

  const checkoutMutation = useMutation({
    mutationFn: () => organizationService.createBillingCheckout(
      `${window.location.origin}/team?checkout=success`,
    ),
    onSuccess: (started) => {
      if (started.adopted_personal_subscription) {
        // The admin's own subscription became the team plan, so there is no
        // Checkout to open and billing is already live — just refresh.
        trackEvent('team_billing_plan_adopted')
        void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
        void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
        return
      }
      trackEvent('team_billing_checkout_opened')
      setClientSecret(started.client_secret ?? null)
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't start checkout. Try again."))
    },
  })
  const portalMutation = useMutation({
    mutationFn: () => organizationService.createBillingPortal(
      `${window.location.origin}/team`,
    ),
    onSuccess: (url) => {
      window.location.href = url
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't open the billing portal."))
    },
  })

  const { billing } = org
  const needsStripeSubscription = billing.is_active
    && !billing.has_stripe_subscription
    && !billing.is_billing_armed
  // A past-due plan is fixed by updating the card in the portal, which pays
  // the open invoice. A new checkout here opened a second subscription on the
  // same card and charged the team twice (GET-19923).
  const isPaymentOverdue = billing.has_stripe_subscription
    && billing.status === 'past_due'
  return (
    <SectionCard
      title="Team billing"
      icon={<CreditCard className="w-5 h-5" />}
      subtitle="One subscription, one card, a seat for everyone on the team."
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {finalizing ? (
            <div className="text-sm text-gray-600 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
              Finalizing your subscription…
            </div>
          ) : isPaymentOverdue ? (
            // Ahead of is_active: a past-due team keeps its seats through the
            // payment grace, and "Active" would hide that the card failed.
            <div className="flex items-center gap-3">
              <Badge label="Payment failed" tone="red" />
              <div className="text-sm text-gray-600">
                {billing.is_active
                  ? "The last payment didn't go through. Update the card under Manage billing to keep the team's seats. Adding a new card retries the open invoice right away."
                  : "The last payment didn't go through, so the team's seats are paused. Update the card under Manage billing and the open invoice is retried."}
              </div>
            </div>
          ) : billing.is_active ? (
            <>
              <Badge
                label={billing.cancel_at_period_end
                  ? 'Ending soon'
                  : billing.status === 'trialing' ? 'Trial' : 'Active'}
                tone={billing.cancel_at_period_end ? 'amber' : 'green'}
              />
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900 tabular-nums">
                  {billing.billed_seats ?? billing.seat_count}
                </span>
                {' '}
                {(billing.billed_seats ?? billing.seat_count) === 1 ? 'seat' : 'seats'}
                {billing.current_period_end ? (
                  <span>
                    {' · '}
                    {billing.cancel_at_period_end || billing.status === 'trialing'
                      ? 'ends'
                      : 'renews'}
                    {' '}
                    {formatDate(billing.current_period_end)}
                  </span>
                ) : null}
              </div>
            </>
          ) : billing.is_billing_armed ? (
            <div className="flex items-center gap-3">
              <Badge label="Ready" tone="green" />
              <div className="text-sm text-gray-600">
                Card on file. No seats to bill yet — members roll onto the team plan as their individual plans end.
              </div>
            </div>
          ) : billing.will_adopt_admin_plan ? (
            <div className="text-sm text-gray-600">
              You're on an individual plan. Moving it onto the team keeps the same card, the same renewal date, and one invoice for everyone.
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              No team subscription yet. Members keep paying individually until you set one up.
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {billing.has_stripe_subscription
          || (billing.has_stripe_customer && !billing.is_active)
          || billing.is_billing_armed ? (
            <button
              type="button"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 text-gray-700 px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {portalMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Manage billing
            </button>
          ) : null}
          {needsStripeSubscription && !finalizing ? (
            <PrimaryButton
              onClick={() => checkoutMutation.mutate()}
              busy={checkoutMutation.isPending}
            >
              Add card
            </PrimaryButton>
          ) : null}
          {!billing.is_active && !billing.is_billing_armed && !isPaymentOverdue && !finalizing ? (
            <PrimaryButton
              onClick={() => checkoutMutation.mutate()}
              busy={checkoutMutation.isPending}
            >
              {billing.will_adopt_admin_plan ? 'Move my plan to the team' : 'Set up team billing'}
            </PrimaryButton>
          ) : null}
        </div>
      </div>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {clientSecret ? (
        <CheckoutModal
          clientSecret={clientSecret}
          onClose={() => {
            setClientSecret(null)
            // Stripe confirms via webhook; refetch shortly after close.
            setTimeout(() => {
              void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
              void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
            }, 1500)
          }}
        />
      ) : null}
    </SectionCard>
  )
}

function AdoptionNotice(): JSX.Element {
  return (
    <p className="mb-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-900">
      When someone accepts, your own plan becomes the team plan — same card,
      same renewal date, with a prorated seat for each person it covers.
    </p>
  )
}

function InvitePanel({ invites, willAdoptAdminPlan }: {
  invites: PendingInviteResponse[]
  // True only for a carded admin on a team that bills nothing yet: accepting
  // their invite moves their own plan onto the team and prorates a seat. The
  // team board discloses that on the billing button, but the decision is made
  // here — and an invite never expires, so a months-old one can trigger it.
  willAdoptAdminPlan: boolean
}): JSX.Element {
  const queryClient = useQueryClient()
  const [emailsInput, setEmailsInput] = useState('')
  const [results, setResults] = useState<InviteResult[]>([])
  const [error, setError] = useState<string | null>(null)

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: STATS_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ELIGIBLE_QUERY_KEY })
  }

  const inviteMutation = useMutation({
    mutationFn: (emails: string[]) => organizationService.createInvites(emails),
    onSuccess: (batch) => {
      trackEvent('team_invites_sent', { count: batch.results.length })
      setResults(batch.results)
      setEmailsInput('')
      refresh()
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't send those invites."))
    },
  })
  const revokeMutation = useMutation({
    mutationFn: (inviteId: number) => organizationService.revokeInvite(inviteId),
    onSuccess: refresh,
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't revoke that invite. Try again."))
    },
  })

  const submit = (): void => {
    setError(null)
    setResults([])
    const emails = emailsInput.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean)
    if (emails.length === 0) return
    inviteMutation.mutate(emails)
  }

  return (
    <SectionCard
      title="Invite people"
      icon={<UserPlus className="w-5 h-5" />}
      subtitle="Anyone on your domain joins instantly; everyone else joins the moment they sign up."
    >
      {willAdoptAdminPlan ? <AdoptionNotice /> : null}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={emailsInput}
          onChange={(e) => setEmailsInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !inviteMutation.isPending) submit()
          }}
          placeholder="jane@yourcompany.com, sam@yourcompany.com"
          className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <PrimaryButton
          onClick={submit}
          disabled={!emailsInput.trim()}
          busy={inviteMutation.isPending}
        >
          Invite
        </PrimaryButton>
      </div>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {results.length > 0 ? (
        <ul className="mt-4 space-y-1.5">
          {results.map((result, index) => (
            <li key={`${result.email}-${index}`} className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">{result.email}</span>
              {' — '}
              {INVITE_OUTCOME_LABELS[result.outcome] ?? result.outcome}
            </li>
          ))}
        </ul>
      ) : null}
      {invites.length > 0 ? (
        <div className="mt-5 border-t border-gray-100 pt-4">
          <h3 className="text-sm font-semibold text-gray-700">Pending invites</h3>
          <ul className="mt-2 divide-y divide-gray-100">
            {invites.map((invite) => (
              <li key={invite.id} className="py-2 flex items-center justify-between gap-3">
                <div className="text-sm text-gray-700">
                  {invite.email}
                  <span className="text-gray-400"> · invited {formatDate(invite.invited_at)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => revokeMutation.mutate(invite.id)}
                  disabled={revokeMutation.isPending}
                  className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SectionCard>
  )
}

/**
 * Splits pasted input into entries. Commas, semicolons and new lines always
 * separate; spaces separate too, except inside a "Jane Doe <jane@acme.com>"
 * chunk copied from a mail client, which the server reads as one address.
 */
function splitRedactedInput(input: string): string[] {
  return input
    .split(/[,;\n]+/)
    .flatMap((chunk) => (chunk.includes('<') ? [chunk] : chunk.split(/\s+/)))
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function describeRedactedEntry(entry: RedactedEntryResponse): string {
  const adder = entry.added_by_name || entry.added_by_email
  const date = formatDate(entry.added_at)
  return adder ? `Added by ${adder} · ${date}` : `Added ${date}`
}

export function RedactedEntriesPanel(): JSX.Element {
  const queryClient = useQueryClient()
  const [entryInput, setEntryInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  const entriesQuery = useQuery({
    queryKey: REDACTED_ENTRIES_QUERY_KEY,
    queryFn: () => organizationService.getRedactedEntries(),
  })
  const entries = entriesQuery.data ?? []

  // Both writes return the whole list as stored, so the board shows what will
  // actually match (normalized, with who added it) rather than what was typed.
  const onSaved = (saved: RedactedEntryResponse[]): void => {
    queryClient.setQueryData(REDACTED_ENTRIES_QUERY_KEY, saved)
  }
  const onFailed = (err: unknown): void => {
    setError(getApiErrorMessage(err, "We couldn't save that. Try again."))
  }
  const addMutation = useMutation({
    mutationFn: (values: string[]) => organizationService.addRedactedEntries(values),
    onSuccess: (saved) => {
      onSaved(saved)
      setEntryInput('')
    },
    onError: onFailed,
  })
  const removeMutation = useMutation({
    mutationFn: (entryId: number) => organizationService.removeRedactedEntry(entryId),
    onSuccess: onSaved,
    onError: (err: unknown) => {
      onFailed(err)
      // Most often another admin removed it first (a 404): refetch so the row
      // the server no longer has doesn't linger next to the error.
      void queryClient.invalidateQueries({ queryKey: REDACTED_ENTRIES_QUERY_KEY })
    },
  })
  const busy = addMutation.isPending || removeMutation.isPending

  const add = (): void => {
    setError(null)
    const values = splitRedactedInput(entryInput)
    if (values.length === 0) return
    addMutation.mutate(values)
  }

  return (
    <SectionCard
      title="Private contacts"
      icon={<EyeOff className="w-5 h-5" />}
      subtitle="Keep specific people, or whole companies, out of what Catch reads."
    >
      <p className="text-sm text-gray-500">
        Add someone's email address, or a whole domain, and your assistant stops
        seeing what they write — for everyone on your team, including the mail
        your team sends them. It still sees that a message or a meeting is there,
        and still keeps those times blocked on your calendar, but the subject, the
        body, the meeting title and their name stay hidden. Your own inbox and
        calendar are unchanged.
      </p>
      {/* Shown before the first entry is added, not after. This is the one
          thing an admin needs to know while they are still deciding: it is a
          rule for what happens next, not a way to take back what has already
          been read. Hiding it until the list is non-empty told them only once
          the decision was behind them. */}
      <p className="mt-3 text-sm text-gray-500">
        This applies from the moment you add someone. Anything your assistant
        read before that stays as it is. Each entry keeps a record of who added
        it and when, so you can show when a deletion request was honoured.
        Removing an entry removes its record too.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={entryInput}
          onChange={(e) => setEntryInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy) add()
          }}
          placeholder="jane@client.com, lawfirm.com"
          aria-label="Email address or domain to keep private"
          className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <PrimaryButton
          onClick={add}
          disabled={!entryInput.trim() || removeMutation.isPending}
          busy={addMutation.isPending}
        >
          Add
        </PrimaryButton>
      </div>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {entries.length > 0 ? (
        <ul className="mt-5 divide-y divide-gray-100 border-t border-gray-100">
          {entries.map((entry) => (
            <li key={entry.id} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {entry.value}
                  <span className="ml-2 font-normal text-gray-400">
                    {entry.is_address ? 'Person' : 'Whole domain'}
                  </span>
                </p>
                <p className="text-xs text-gray-500">{describeRedactedEntry(entry)}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  removeMutation.mutate(entry.id)
                }}
                disabled={busy}
                aria-label={`Remove ${entry.value}`}
                className="shrink-0 text-sm font-medium text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 text-sm text-gray-400">No one is private yet.</p>
      )}
    </SectionCard>
  )
}

function MigratePanel({ eligible, willAdoptAdminPlan }: {
  eligible: EligibleUserResponse[]
  // Migrating writes the clicking admin as the inviter, so an accepted
  // migration adopts their plan exactly as a typed invite does.
  willAdoptAdminPlan: boolean
}): JSX.Element | null {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const migrateMutation = useMutation({
    mutationFn: (userIds: number[]) => organizationService.migrateMembers(userIds),
    onSuccess: (batch) => {
      trackEvent('team_members_migrated', { count: batch.results.length })
      setSelected(new Set())
      void queryClient.invalidateQueries({ queryKey: ELIGIBLE_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: STATS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
    },
  })

  if (eligible.length === 0) return null

  const toggle = (userId: number): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  return (
    <SectionCard
      title="Already on Catch"
      icon={<Users className="w-5 h-5" />}
      subtitle="People from your domain with their own Catch accounts. Invite them — they join (and move onto team billing) once they accept."
    >
      {willAdoptAdminPlan ? <AdoptionNotice /> : null}
      <ul className="divide-y divide-gray-100">
        {eligible.map((candidate) => (
          <li key={candidate.user_id} className="py-2.5 flex items-center gap-3">
            <input
              type="checkbox"
              checked={selected.has(candidate.user_id)}
              onChange={() => toggle(candidate.user_id)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            {candidate.picture ? (
              <img src={candidate.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-gray-100" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {candidate.name || candidate.email}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {candidate.email} · joined {formatDate(candidate.member_since)}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <PrimaryButton
          onClick={() => migrateMutation.mutate(Array.from(selected))}
          disabled={selected.size === 0}
          busy={migrateMutation.isPending}
        >
          Invite
          {selected.size > 0 ? ` ${selected.size}` : ''}
          {' '}
          to the team
        </PrimaryButton>
      </div>
    </SectionCard>
  )
}

function MembersTable({ members, stats, currentUserId }: {
  members: OrganizationMemberResponse[]
  stats: OrganizationStatsResponse | undefined
  currentUserId: number | undefined
}): JSX.Element {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const statsByUser = useMemo(
    () => new Map((stats?.members ?? []).map((m) => [m.user_id, m])),
    [stats],
  )

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: STATS_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ELIGIBLE_QUERY_KEY })
  }
  const onError = (err: unknown): void => {
    setError(getApiErrorMessage(err, "That didn't go through. Try again."))
  }

  const deactivateMutation = useMutation({
    mutationFn: (userId: number) => organizationService.deactivateMember(userId),
    onSuccess: () => {
      trackEvent('team_member_deactivated')
      refresh()
    },
    onError,
  })
  const reactivateMutation = useMutation({
    mutationFn: (userId: number) => organizationService.reactivateMember(userId),
    onSuccess: () => {
      trackEvent('team_member_reactivated')
      refresh()
    },
    onError,
  })
  const promoteMutation = useMutation({
    mutationFn: (userId: number) => organizationService.promoteMember(userId),
    onSuccess: () => {
      trackEvent('team_member_promoted')
      refresh()
    },
    onError,
  })
  const removeMutation = useMutation({
    mutationFn: (userId: number) => organizationService.removeMember(userId),
    onSuccess: () => {
      trackEvent('team_member_removed')
      refresh()
    },
    onError,
  })
  const busy = deactivateMutation.isPending
    || reactivateMutation.isPending
    || promoteMutation.isPending
    || removeMutation.isPending

  const confirmDeactivate = (member: OrganizationMemberResponse): void => {
    setError(null)
    const label = member.name || member.email
    if (window.confirm(
      `Close ${label}'s Catch account? Their assistant stops working and they're signed out everywhere. You can reactivate them later.`,
    )) {
      deactivateMutation.mutate(member.user_id)
    }
  }
  const confirmRemove = (member: OrganizationMemberResponse): void => {
    setError(null)
    const label = member.name || member.email
    if (window.confirm(
      `Take ${label} off the team? Their account stays active, but they'll be billed individually from now on.`,
    )) {
      removeMutation.mutate(member.user_id)
    }
  }
  const confirmPromote = (member: OrganizationMemberResponse): void => {
    setError(null)
    const label = member.name || member.email
    if (window.confirm(
      `Promote ${label} to team admin? They'll be able to invite and manage members and team billing.`,
    )) {
      promoteMutation.mutate(member.user_id)
    }
  }

  return (
    <SectionCard
      title="Members"
      icon={<Users className="w-5 h-5" />}
      subtitle="Everyone on your team, what they're on, and how they're billed."
    >
      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-4 font-medium">Member</th>
              <th className="py-2 pr-4 font-medium">Role</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Billing</th>
              <th className="py-2 pr-4 font-medium text-right">Handled</th>
              <th className="py-2 pr-4 font-medium text-right">Hours saved</th>
              <th className="py-2 pr-4 font-medium">Last active</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {members.map((member) => {
              const usage = statsByUser.get(member.user_id)
              const isSelf = member.user_id === currentUserId
              return (
                <tr key={member.user_id}>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2.5 min-w-[12rem]">
                      {member.picture ? (
                        <img src={member.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {member.name || member.email}
                          {isSelf ? <span className="text-gray-400 font-normal"> (you)</span> : null}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4 capitalize text-gray-600">{member.role}</td>
                  <td className="py-3 pr-4">
                    <StatusBadge member={member} />
                  </td>
                  <td className="py-3 pr-4"><BillingBadge source={member.billing_source} /></td>
                  <td className="py-3 pr-4 text-right tabular-nums text-gray-900">
                    {usage ? usage.actions.toLocaleString('en-US') : '—'}
                  </td>
                  <td className="py-3 pr-4 text-right tabular-nums text-gray-900">
                    {usage ? usage.hours_saved.toLocaleString('en-US') : '—'}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap text-gray-600">
                    {usage?.active_this_week ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        This week
                      </span>
                    ) : (
                      formatDate(usage?.last_active_at)
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    {!isSelf ? (
                      <div className="inline-flex items-center gap-1">
                        {member.role === 'member' && member.user_status === 'active' ? (
                          <button
                            type="button"
                            title="Promote to team admin"
                            aria-label={`Promote ${member.name || member.email} to team admin`}
                            disabled={busy}
                            onClick={() => confirmPromote(member)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                          >
                            <ShieldCheck className="w-4 h-4" />
                          </button>
                        ) : null}
                        {member.role !== 'admin' && member.user_status === 'disabled' ? (
                          <button
                            type="button"
                            title="Reactivate account"
                            disabled={busy}
                            onClick={() => reactivateMutation.mutate(member.user_id)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        ) : member.role !== 'admin' ? (
                          <button
                            type="button"
                            title="Close account"
                            disabled={busy}
                            onClick={() => confirmDeactivate(member)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          title="Remove from team"
                          disabled={busy}
                          onClick={() => confirmRemove(member)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}

function MemberView({ org }: { org: OrganizationResponse }): JSX.Element {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const leaveMutation = useMutation({
    mutationFn: () => organizationService.leaveOrganization(),
    onSuccess: () => {
      trackEvent('team_left')
      void queryClient.invalidateQueries({ queryKey: ORG_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: MY_INVITES_QUERY_KEY })
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, "We couldn't remove you from the team. Try again."))
    },
  })
  const confirmLeave = (): void => {
    if (window.confirm(`Leave the ${org.name} team? Your account stays — you'll just be billed individually again.`)) {
      leaveMutation.mutate()
    }
  }

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-soft p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center">
          <Building2 className="w-6 h-6" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">
          You're on the {org.name} team
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          {org.billing.is_active
            ? 'Your subscription is covered by your team.'
            : 'Team membership and billing are managed by your team admin.'}
        </p>
        <button
          type="button"
          disabled={leaveMutation.isPending}
          onClick={confirmLeave}
          className="mt-6 inline-flex items-center gap-2 rounded-xl border border-gray-300 text-gray-600 px-4 py-2 text-sm font-medium hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors disabled:opacity-60"
        >
          {leaveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Leave team
        </button>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </div>
    </div>
  )
}

export function TeamPage(): JSX.Element {
  const { user } = useAuth()
  const access = useOrganizationAccess()

  const orgQuery = useQuery({
    queryKey: ORG_QUERY_KEY,
    queryFn: () => organizationService.getMyOrganization(),
    enabled: access.enabled,
  })
  const org = orgQuery.data ?? null
  const isAdmin = org?.my_role === 'admin'

  const membersQuery = useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: () => organizationService.getMembers(),
    enabled: isAdmin,
  })
  const statsQuery = useQuery({
    queryKey: STATS_QUERY_KEY,
    queryFn: () => organizationService.getStats(),
    enabled: isAdmin,
  })
  const invitesQuery = useQuery({
    queryKey: INVITES_QUERY_KEY,
    queryFn: () => organizationService.getPendingInvites(),
    enabled: isAdmin,
  })
  const eligibleQuery = useQuery({
    queryKey: ELIGIBLE_QUERY_KEY,
    queryFn: () => organizationService.getEligibleUsers(),
    enabled: isAdmin,
  })
  const myInvitesQuery = useQuery({
    queryKey: MY_INVITES_QUERY_KEY,
    queryFn: () => organizationService.getMyInvites(),
    enabled: access.enabled && !orgQuery.isLoading && !org,
  })

  if (access.isLoading || (access.enabled && orgQuery.isLoading)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  if (!access.enabled) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <AlertCircle className="w-10 h-10 text-primary-500 mb-3" />
        <h1 className="text-lg font-semibold text-gray-900">
          Team management isn't available on your account yet
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Talk to us if you'd like early access for your company.
        </p>
      </div>
    )
  }

  const stats = statsQuery.data

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-5xl">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            {org ? org.name : 'Team'}
          </h1>
          <p className="mt-1 text-gray-500">
            {org
              ? `Manage your team in one place${org.email_domain ? ` · ${org.email_domain}` : ''}`
              : 'One board for your whole company on Catch.'}
          </p>
        </div>

        {!org && (myInvitesQuery.data?.length ?? 0) > 0 ? (
          <MyInvitesCard invites={myInvitesQuery.data ?? []} />
        ) : null}
        {!org ? <CreateTeamCard /> : null}
        {!org && access.hasWorkspaceTenant ? (
          <NotACompanyCard declared={access.autoJoinDisabled} />
        ) : null}
        {org && !isAdmin ? (
          <div className="space-y-6">
            <MemberView org={org} />
            <TeamSkillsSection />
          </div>
        ) : null}

        {org && isAdmin ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <OverviewTile
                label="Hours saved"
                value={(stats?.hours_saved_total ?? 0).toLocaleString('en-US')}
                Icon={Clock}
              />
              <OverviewTile
                label="Things handled"
                value={(stats?.actions_total ?? 0).toLocaleString('en-US')}
                Icon={Zap}
              />
              <OverviewTile
                label="Active this week"
                value={`${stats?.weekly_active_count ?? 0}/${stats?.member_count ?? org.member_count}`}
                Icon={BarChart3}
              />
              <OverviewTile
                label="Seats"
                value={String(org.billing.seat_count)}
                Icon={Users}
              />
            </div>

            <BillingCard org={org} />

            <MembersTable
              members={membersQuery.data ?? []}
              stats={stats}
              currentUserId={user?.id}
            />

            <InvitePanel
              invites={invitesQuery.data ?? []}
              willAdoptAdminPlan={org.billing.will_adopt_admin_plan}
            />

            <MigratePanel
              eligible={eligibleQuery.data ?? []}
              willAdoptAdminPlan={org.billing.will_adopt_admin_plan}
            />

            <TeamSkillsSection />

            {access.domainRedactionEnabled ? <RedactedEntriesPanel /> : null}

            {org.auto_join_enabled ? (
              <NotACompanyCard declared={false} org={org} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
